import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import Alert from '../Alert';
import { fmtDateTime, fmtDate, addWorkingDays, daysPastDeadline } from '../../lib/dates';
import { formatGBP } from '../../lib/money';
import { notifyOwnersOfUpdateRequest } from '../../lib/briefQuestionNotify';

// Supplier post-deadline status + update request (after-the-deadline brief
// §1/§2). Only for a supplier who SUBMITTED, on a brief past its response
// deadline with no outcome recorded. Silence is what makes people chase, so the
// brief says what's happening on its own:
//   • day after the deadline → blue "reviewing the responses" (no control)
//   • 3 working days after → amber, gains "Ask for an update" + optional line
//   • after asking → confirmation, rate-limited to once per 7 days
// No decision-by date (Rob's call), so no "you'll hear by" line.

const SEVEN_DAYS = 7 * 86400000;

export default function BriefPostDeadline({
  briefId,
  supplierId,
  supplierUserId,
  deadlineIso,      // effective response deadline (per-recipient override or brief)
  hasOutcome,       // brief awarded/closed/cancelled — recording it removes this entirely
  isSubmitted,      // only suppliers who submitted see any of this
  quoteAmount,      // optional — "your quote of £Y still stands"
  decisionBy,       // brief.decision_by (ISO date) — set by a holding update (§4)
  briefName,
  supplierName,
}) {
  const [lastRequest, setLastRequest] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState(null); // latest visible holding update
  const [loaded, setLoaded] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const active = isSubmitted && !hasOutcome && !!deadlineIso && Date.now() > new Date(deadlineIso).getTime();

  const load = useCallback(async () => {
    if (!active || !supplierId) { setLoaded(true); return; }
    const [reqRes, updRes] = await Promise.all([
      supabase
        .from('brief_update_requests')
        .select('id, created_at')
        .eq('brief_id', briefId).eq('supplier_id', supplierId)
        .order('created_at', { ascending: false }).limit(1),
      // Latest holding update this supplier is allowed to see (RLS-filtered).
      supabase
        .from('brief_status_updates')
        .select('id, message, created_at, decision_by, author:internal_users!author_user_id(full_name)')
        .eq('brief_id', briefId)
        .order('created_at', { ascending: false }).limit(1),
    ]);
    setLastRequest((reqRes.data || [])[0] || null);
    setStatusUpdate((updRes.data || [])[0] || null);
    setLoaded(true);
  }, [active, briefId, supplierId]);

  useEffect(() => { load(); }, [load]);

  if (!active || !loaded) return null;

  const deadline = new Date(deadlineIso);
  const past3wd = Date.now() > addWorkingDays(deadline, 3).getTime();
  const daysOverdue = Math.floor((Date.now() - deadline.getTime()) / 86400000);
  const canAskAgainAt = lastRequest ? new Date(lastRequest.created_at).getTime() + SEVEN_DAYS : null;
  const rateLimited = canAskAgainAt != null && Date.now() < canAskAgainAt;

  const quoteLine = quoteAmount != null && quoteAmount !== ''
    ? `Your quote of ${formatGBP(quoteAmount)} ex-VAT still stands.`
    : 'Your quote still stands.';

  const send = async () => {
    setSending(true);
    const { error } = await supabase.from('brief_update_requests').insert([{
      brief_id: briefId, supplier_id: supplierId, requested_by_user_id: supplierUserId || null,
      message: message.trim() || null,
    }]);
    if (error) { setSending(false); return; }
    notifyOwnersOfUpdateRequest({ briefId, briefName, supplierName, message: message.trim() }).catch(() => {});
    setMessage('');
    setSending(false);
    load();
  };

  // A holding update set an expected decision date still in the future (§4):
  // blue alert naming the date (+ the message, attributed), and the ask control
  // is suppressed until that date passes. A missed date returns to the chase.
  const decisionByFuture = !!decisionBy && daysPastDeadline(decisionBy) < 0;
  if (decisionByFuture) {
    const author = statusUpdate?.author?.full_name;
    const msg = statusUpdate?.message && statusUpdate.message.trim();
    return (
      <div className="mb-6">
        <Alert
          tone="blue"
          title={`Fusion 2K expects to decide by ${fmtDate(decisionBy)}`}
          body={
            <>
              {`${quoteLine} No action needed — they'll be in touch either way.`}
              {msg && (
                <span className="block mt-2 pl-3 border-l-2 border-[#C4DEE6] text-[#2A5B6B]">
                  “{msg}”{author ? <span className="block text-[12px] text-[#51534A] mt-0.5">— {author}, Fusion 2K</span> : null}
                </span>
              )}
            </>
          }
        />
      </div>
    );
  }

  // Day 1–2 after the deadline: informational, no control.
  if (!past3wd) {
    return (
      <div className="mb-6">
        <Alert
          tone="blue"
          title="Fusion 2K is reviewing the responses"
          body={`The deadline passed on ${fmtDateTime(deadlineIso)}. ${quoteLine} You'll hear from Fusion 2K once a decision is made.`}
        />
      </div>
    );
  }

  // Already asked within the last 7 days — confirmation, not the control (§2).
  if (rateLimited) {
    return (
      <div className="mb-6">
        <div className="flex items-start gap-3 bg-[#E9F3DC] border border-[#C9E3AC] rounded-xl px-4 py-3">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3B6D11" strokeWidth="2.6" className="flex-shrink-0 mt-0.5" aria-hidden="true"><path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          <p className="font-['Metropolis'] text-sm text-[#2C5A0A]">
            You asked for an update on <strong>{fmtDateTime(lastRequest.created_at)}</strong>. The Fusion 2K team has been emailed. You can ask again from <strong>{fmtDate(new Date(canAskAgainAt).toISOString())}</strong>.
          </p>
        </div>
      </div>
    );
  }

  // 3+ working days, not rate-limited: the amber ask-for-an-update card.
  return (
    <div className="mb-6 bg-[#FBF0DC] border border-[#EFD9A9] rounded-xl p-4">
      <div className="flex items-start gap-3">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9A6208" strokeWidth="2" className="flex-shrink-0 mt-0.5" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.01" strokeLinecap="round" /></svg>
        <div className="min-w-0 flex-1">
          <p className="font-['Metropolis'] text-[15px] font-semibold text-[#5E3D05]">Still no decision on this one</p>
          <p className="font-['Metropolis'] text-[13.5px] text-[#6B4A11] mt-0.5">The deadline passed {daysOverdue} day{daysOverdue === 1 ? '' : 's'} ago. You can ask Fusion 2K for an update.</p>
          <div className="mt-3">
            <label htmlFor="update-req-msg" className="block font-['Metropolis'] text-[12.5px] font-semibold text-[#6B4A11] mb-1">Anything they should know? (optional)</label>
            <input
              id="update-req-msg"
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={sending}
              placeholder="e.g. We can hold the crew and truck until 8 Sep."
              className="w-full bg-white border border-[#EFD9A9] rounded-lg text-ink px-3 py-2 font-['Metropolis'] text-sm focus:outline-none focus:border-[#9A6208] disabled:opacity-60" />
            <p className="font-['Metropolis'] text-[11.5px] text-[#6B4A11] mt-1">This goes to the brief owner by email. Telling them how long you can hold the work helps them prioritise.</p>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={send}
                disabled={sending}
                className="rounded-full font-['Metropolis'] text-[13.5px] font-semibold px-4 py-2 transition-colors bg-[#2F6E14] text-white hover:bg-[#3B6D11] disabled:opacity-50">
                {sending ? 'Sending…' : 'Ask for an update'}
              </button>
              <span className="font-['Metropolis'] text-[12.5px] text-[#6B4A11]">You can ask again after a week.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
